import { Helmet } from "react-helmet-async";

const DEFAULT_TITLE = "CareSenta — The operating system for modern healthcare";
const DEFAULT_DESCRIPTION =
  "Six HIPAA-compliant AI modules — prior auth, scribe, no-show, refills, care gaps, denials — running quietly inside your existing EHR.";
const SITE_URL = "https://caresenta.com";
const OG_IMAGE = `${SITE_URL}/og-cover.png`;

/**
 * Per-page SEO tags. Use in every top-level page component.
 *
 *   <SEO
 *     title="Prior Auth Autopilot — CareSenta"
 *     description="Cut prior-auth admin work by 80% ..."
 *     path="/modules/prior-auth"
 *     jsonLd={softwareApplicationSchema()}
 *   />
 */
export default function SEO({
  title,
  description,
  path = "",
  image = OG_IMAGE,
  noindex = false,
  jsonLd = null,
}) {
  const fullTitle = title ? `${title}` : DEFAULT_TITLE;
  const desc = description || DEFAULT_DESCRIPTION;
  const url = path ? `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}` : SITE_URL;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex,follow" />}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="CareSenta" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={image} />

      {/* Structured data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}

/* ---------- JSON-LD schema helpers ---------- */

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    "name": "CareSenta",
    "url": SITE_URL,
    "logo": `${SITE_URL}/apple-touch-icon.svg`,
    "sameAs": [],
    "description": DEFAULT_DESCRIPTION,
    "contactPoint": [{
      "@type": "ContactPoint",
      "email": "inquiries@caresenta.com",
      "contactType": "Sales",
      "availableLanguage": ["English"],
    }],
  };
}

export function softwareApplicationSchema({ name, description, url, features = [], price }) {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": name,
    "applicationCategory": "MedicalApplication",
    "operatingSystem": "Web",
    "description": description,
    "url": url,
    "offers": price ? {
      "@type": "Offer",
      "price": price,
      "priceCurrency": "USD",
      "priceValidUntil": "2027-12-31",
    } : undefined,
    "featureList": features,
    "provider": { "@id": `${SITE_URL}/#organization` },
  };
}

export function faqSchema(questions) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": questions.map((q) => ({
      "@type": "Question",
      "name": q.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": q.a,
      },
    })),
  };
}

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((it, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": it.name,
      "item": it.item.startsWith("http") ? it.item : `${SITE_URL}${it.item}`,
    })),
  };
}

export function articleSchema({ title, description, url, date, author = "CareSenta" }) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "url": url,
    "datePublished": date,
    "author": { "@type": "Organization", "name": author },
    "publisher": { "@id": `${SITE_URL}/#organization` },
  };
}
